import type {
  Candidate,
  User,
  SavedCandidate,
  AfcCandidateSearch,
} from '@axiom/validation';
import { CountryUtil, CurrencyUtil } from '@axiom/utils';

import { isClientUser, isEnvoyUser } from './roles-util';
import { localStorageUtil } from './local-storage-util';

type CandidateP = Candidate | SavedCandidate['candidate'] | AfcCandidateSearch;

const isCompleteCandidate = (candidate: CandidateP): candidate is Candidate => {
  return 'currencyType' in candidate && 'hoursPerDay' in candidate;
};

export type PricingData = {
  pricingAvailable: boolean;
  fullTimeDailyRate?: string | null;
  adHocHourlyRate?: string | null;
};

export const PricingUtil = {
  getPricingData(candidate: CandidateP, user?: User | null): PricingData {
    const { displayLowHourlyRate, displayHighHourlyRate, addressCountry } =
      candidate;
    const currencyType = isCompleteCandidate(candidate)
      ? candidate.currencyType
      : undefined;
    const hoursPerDay = isCompleteCandidate(candidate)
      ? candidate.hoursPerDay
      : undefined;
    const formattedDisplayLowHourlyRate =
      typeof displayLowHourlyRate === 'string'
        ? +displayLowHourlyRate
        : displayLowHourlyRate;

    const formattedDisplayHighHourlyRate =
      typeof displayHighHourlyRate === 'string'
        ? +displayHighHourlyRate
        : displayHighHourlyRate;

    const pricingUnavailable = {
      pricingAvailable: false,
    };
    if (
      !currencyType ||
      !addressCountry ||
      !hoursPerDay ||
      typeof formattedDisplayLowHourlyRate !== 'number' ||
      !formattedDisplayLowHourlyRate
    ) {
      return pricingUnavailable;
    }
    const fullTimeDailyRate = `${CurrencyUtil.getFormattedCurrency({ value: Math.ceil(formattedDisplayLowHourlyRate * hoursPerDay), currencyCode: currencyType })}/day`;

    const noAdHocCountry = CountryUtil.isNoAdHocCountry(addressCountry);
    if (
      !noAdHocCountry &&
      (typeof formattedDisplayHighHourlyRate !== 'number' ||
        !formattedDisplayHighHourlyRate)
    ) {
      return pricingUnavailable;
    }

    const adHocHourlyRate = noAdHocCountry
      ? null
      : `${CurrencyUtil.getFormattedCurrency({ value: formattedDisplayHighHourlyRate ? Math.ceil(formattedDisplayHighHourlyRate) : null, currencyCode: currencyType })}/hr`;
    const pricingEnabledForUser =
      (isClientUser(user) && user?.contact?.canViewPricing) ||
      (isEnvoyUser(user) && localStorageUtil.showPricing().get() === true);
    return {
      fullTimeDailyRate,
      adHocHourlyRate,
      pricingAvailable: !!(fullTimeDailyRate && pricingEnabledForUser),
    };
  },
};
